import {
  Component,
  Element,
  getAssetPath,
  Host,
  h,
  Prop,
  State,
  Method
} from "@stencil/core";

import {
  ChCheckboxCustomEvent,
  TreeViewItemModel,
  TreeViewItemModelExtended
} from "@genexus/chameleon-controls-library";

import {
  checkItems,
  convertItemsListToTreeViewModel,
  convertItemsNodeListToFlattenedTreeViewModel,
  expandItems,
  searchParentsItems,
  selectItems,
  unselectAllNodes,
  findFirstActiveNode
} from "./helpers";
import { getIconPath, MercuryBundles } from "@genexus/mercury";
import { Locale } from "../../common/locale";
import { ContextMenuInfo } from "../../common/types";
import { RadioGroupItemModel } from "@genexus/chameleon-controls-library/dist/types/components/radio-group/types";

import { ACTIVE_ITEM_DEFAULT_ICON } from "./helpers";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "chameleon/scrollbar",
  "components/tooltip",
  "components/tree-view",
  "utils/form--full",
  "utils/layout",
  "utils/spacing",
  "utils/typography"
];

const SYSTEM_SEARCH_ICON = getIconPath({
  category: "system",
  name: "search",
  colorType: "on-surface"
});

const VIEW_AS_TREE_ID: NodesVersionsViewType = "tree";
const VIEW_AS_LIST_ID: NodesVersionsViewType = "list";

const TOGGLE_CHECKBOXES_CHECKED_VALUE = "on";

@Component({
  tag: "gx-ide-select-kb-items",
  styleUrl: "select-kb-items.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/select-kb-items"]
})
export class GxIdeSelectKbItems {
  #_componentLocale: any;
  #chShortcutsEl: HTMLChShortcutsElement;
  #selectedNodesIds: string[] = [];
  #shortcutsSrc = getAssetPath(
    `./gx-ide-assets/select-kb-items/shortcuts.json`
  );
  #updatedModelList: ItemNode[] = [];
  #viewItemsAsModel: RadioGroupItemModel[];
  #treeViewRenderEl: HTMLChTreeViewRenderElement;

  @Element() el: HTMLGxIdeSelectKbItemsElement;

  /**
   * Represents the active version node caption and icon (if any)
   */
  @State() activeItemNode: ActiveNode;

  /**
   * Represents the selected nodes ids
   */
  @State() checkedNodesIds: string[] = [];

  /**
   * If true, the footer will be rendered
   */
  @State() renderFooter: boolean = false;

  /**
   * Represents the nodes list model
   */
  @State() nodeListFlattenedTreeModel: TreeViewItemModel[] = [];

  /**
   * To show a loader when the component is loading
   */
  @State() loading: boolean = true;

  /**
   * View type for the nodes.
   */
  @State() nodesVersionsViewType: NodesVersionsViewType = "tree";

  /**
   * Represents the nodes tree model
   */
  @State() nodeVersionsListTreeModel: TreeViewItemModel[] = [];

  /**
   * "Search Pattern" input value, used to filter the nodes tree/list.
   */
  @State() searchPatternValue: string = null;

  /**
   * It maps to the ch-tree-view-render "toggleCheckboxes" property.
   */
  @State() toggleCheckboxes: boolean;

  /**
   * Represents the itemLis list model
   */
  @State() itemsList: ItemNode[] = [];

  /**
   * Callback emitted when the user clicks on the "Active item"
   */
  @Prop() readonly activeItemCallback?: () => Promise<void>;

  /**
   * Callback emitted when the 'cancel' button is pressed
   */
  @Prop() readonly cancelCallback?: () => Promise<void>;

  /**
   * Defines which nodes of the component have checkboxes.
   */
  @Prop() readonly checkboxType: CheckboxType;

  /**
   * Define if a item node is expanded on click interaction. If false item-node will only be expanded on double click.
   */
  @Prop() readonly expandOnClick: boolean = true;

  /**
   * Callback emitted when the items checked items change
   */
  @Prop() readonly checkedChanged?: (checkedItems: string[]) => Promise<void>;

  /**
   * Callback emitted when the 'select' button is pressed
   */
  @Prop() readonly confirmCallback?: (checkedItems: string[]) => Promise<void>;

  /**
   * Set of additional actions (callbacks) to those already provided by default by the selector (cancelCallback and selectCallback)
   */
  @Prop() readonly dialogActions: DialogAction[] = [];

  /**
   * Callback emitted when the user right click on an item
   */
  @Prop() readonly itemContextMenuCallback: (
    info: ContextMenuInfo
  ) => Promise<void>;

  /**
   * Callback to load the items list model
   */
  @Prop() readonly loadItemsCallback!: () => Promise<ItemNode[]>;

  /**
   * Used to define if the component must show the "Toggle Checkboxes" option or not
   */
  @Prop() readonly showToggleCheckboxes: boolean = true;

  /**
   * This define if the selection is unique or not
   */
  @Prop() readonly multiSelection?: boolean = false;

  /**
   * This define if the checks are unique or not
   */
  @Prop() readonly singleChecks?: boolean = false;

  /**
   * Used to define if the component must show the "Active item" link or not
   */
  @Prop() readonly showActiveItemLink?: boolean = false;

  /**
   * Defines whether to show a loader while loading items
   */
  @Prop() readonly useLoader?: boolean = true;

  /**
   * Callback emitted when the items selection change
   */
  @Prop() readonly selectionChanged?: (selectedItem: string[]) => Promise<void>;

  async componentWillLoad() {
    this.#_componentLocale = await Locale.getComponentStrings(this.el);
    this.toggleCheckboxes = this.showToggleCheckboxes;
    this.#viewItemsAsModel = [
      { value: VIEW_AS_TREE_ID, caption: this.#_componentLocale.main.tree },
      { value: VIEW_AS_LIST_ID, caption: "List" }
    ];
    this.renderFooter =
      this.dialogActions.length > 0 ||
      !!this.cancelCallback ||
      !!this.confirmCallback;

    this.#loadItems();
  }

  @Method()
  async reloadItems() {
    await this.#loadItems();
  }

  /**
   * Suspends or reactivates the shortcuts
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    if (suspendShortcuts) {
      this.#chShortcutsEl.suspend = true;
    } else {
      this.#chShortcutsEl.suspend = false;
    }
  }

  /**
   * Allow to update the selected items
   */
  @Method()
  async updateSelection(selectedItemsIds: string[]) {
    // first clear selected
    this.#treeViewRenderEl.updateItemsProperties(this.#selectedNodesIds, {
      selected: false
    });
    // then select
    this.#treeViewRenderEl.updateItemsProperties(selectedItemsIds, {
      selected: true
    });
  }

  /**
   * Allow to update the checked items
   */
  @Method()
  async updateCheckedItems(checkedItemsIds: string[]) {
    // first clear checks
    this.#treeViewRenderEl.updateItemsProperties(this.checkedNodesIds, {
      checked: false
    });
    // then select
    this.#treeViewRenderEl.updateItemsProperties(checkedItemsIds, {
      checked: true
    });
    this.checkedNodesIds = checkedItemsIds;
  }

  async #loadItems() {
    try {
      this.loading = true;
      this.itemsList = await this.loadItemsCallback();
      this.#updateViews(this.itemsList);
      // Check and update activeItem
      this.activeItemNode = findFirstActiveNode(this.itemsList);
    } catch (error) {
      console.error("Error loading items:", error);
    } finally {
      this.loading = false;
    }
  }

  #cancelHandler = () => {
    if (this.cancelCallback) {
      this.cancelCallback();
    }
  };

  #checkedItemsChangeHandler = async (
    e: CustomEvent<Map<string, TreeViewItemModelExtended>>
  ) => {
    let checkedNodesIds: string[] = [];
    e.detail.forEach(node => {
      if (node.item.checked) {
        checkedNodesIds.push(node.item.id);
      }
    });

    if (this.singleChecks) {
      // Detect the last change by comparing previous and new state
      let lastCheckedId: string | null = null;
      // If there are more in the new state, it was a check; if less, it was an uncheck
      if (checkedNodesIds.length > this.checkedNodesIds.length) {
        // Find the newly checked item
        lastCheckedId =
          checkedNodesIds.find(id => !this.checkedNodesIds.includes(id)) ||
          null;
      } else if (checkedNodesIds.length < this.checkedNodesIds.length) {
        // Find the unchecked item (not used in single check, but kept for clarity)
        lastCheckedId =
          this.checkedNodesIds.find(id => !checkedNodesIds.includes(id)) ||
          null;
      }

      // Only allow one if multiChecks is false
      if (this.singleChecks && checkedNodesIds.length > 1) {
        // If we detected the last checked, keep it; otherwise, keep the first
        checkedNodesIds = lastCheckedId
          ? [lastCheckedId]
          : [checkedNodesIds[0]];
        // Uncheck the others
        this.#treeViewRenderEl.updateItemsProperties(
          Array.from(e.detail.keys()).filter(id => id !== checkedNodesIds[0]),
          { checked: false }
        );
      }
    }

    this.checkedNodesIds = checkedNodesIds;
    this.#updatedModelList = checkItems(this.itemsList, this.checkedNodesIds);

    if (this.checkedChanged) {
      this.checkedChanged(this.checkedNodesIds);
    }
  };

  #confirmHandler = () => {
    if (this.confirmCallback) {
      this.confirmCallback(this.checkedNodesIds);
    }
  };

  #contextMenuHandler = async (
    e: CustomEvent<{
      id: string;
      itemRef: HTMLChTreeViewItemElement;
      metadata: string;
      contextmenuEvent: PointerEvent;
    }>
  ) => {
    if (this.itemContextMenuCallback) {
      let info: ContextMenuInfo;
      const isSelected = this.#selectedNodesIds.find(id => id === e.detail.id);
      if (!this.#selectedNodesIds.length || !isSelected) {
        await this.#treeViewRenderEl.updateItemsProperties(
          this.#selectedNodesIds,
          { selected: false }
        );
        await this.#treeViewRenderEl.updateItemsProperties([e.detail.id], {
          selected: true
        });
        info = {
          clientX: e.detail.contextmenuEvent.clientX,
          clientY: e.detail.contextmenuEvent.clientY,
          selection: [e.detail.id]
        };
      } else {
        info = {
          clientX: e.detail.contextmenuEvent.clientX,
          clientY: e.detail.contextmenuEvent.clientY,
          selection: this.#selectedNodesIds
        };
      }
      this.itemContextMenuCallback(info);
    }
  };

  #executeDialogAction = (
    callback: (checkedNodesIds: string[], selectedNodesIds: string[]) => void
  ) => {
    return () => {
      callback(this.checkedNodesIds, this.#selectedNodesIds);
    };
  };

  #patternSearchValueChangedHandler = (
    event: CustomEvent<string> | InputEvent
  ) => {
    this.searchPatternValue = event.detail.toString();
  };

  #renderActionButtons = (): HTMLElement[] => {
    return (
      <div>
        {this.dialogActions.map(action => {
          return (
            <button
              class={"button-tertiary"}
              part={`button button-${action.name}`}
              onClick={this.#executeDialogAction(action.callback)}
              disabled={this.loading}
            >
              {action.name}
            </button>
          );
        })}
      </div>
    );
  };

  #renderFooter = (): HTMLElement => {
    return (
      <footer class="footer dialog-footer-with-border spacing-body-inline spacing-body-block-end dialog-footer-space-between">
        {this.#renderActionButtons()}
        <div class="buttons-spacer ">
          {this.cancelCallback && (
            <button
              class={"button-secondary"}
              part={"cancel-button"}
              onClick={this.#cancelHandler}
            >
              {this.#_componentLocale.footer.cancel}
            </button>
          )}
          {this.confirmCallback && (
            <button
              class={"button-primary"}
              part={"confirm-button"}
              onClick={this.#confirmHandler}
              disabled={this.loading || this.checkedNodesIds?.length === 0}
            >
              {this.#_componentLocale.footer.select}
            </button>
          )}
        </div>
      </footer>
    );
  };

  #selectedItemsChangeHandler = (
    e: CustomEvent<TreeViewItemModelExtended[]>
  ) => {
    const selectedNodesIds: string[] = [];
    unselectAllNodes(this.itemsList);
    e.detail.forEach(node => {
      selectedNodesIds.push(node.item.id);
    });
    this.#selectedNodesIds = selectedNodesIds;
    if (this.selectionChanged) {
      this.selectionChanged(this.#selectedNodesIds);
    }

    this.#updatedModelList = selectItems(this.itemsList, selectedNodesIds);
  };

  #toggleAndSelectNodesInModelItems = async (selectedItemsIds: string[]) => {
    const nodesToToggle = searchParentsItems(this.itemsList, selectedItemsIds);
    const newItemsSelected = selectItems(this.itemsList, selectedItemsIds);
    const newItemsExpanded = expandItems(newItemsSelected, nodesToToggle);
    this.#updateViews(newItemsExpanded);
  };

  #toggleCheckedNodesInModelItems = async (selectedItemsIds: string[]) => {
    const nodesToToggle = searchParentsItems(this.itemsList, selectedItemsIds);
    const newItemsExpanded = expandItems(this.itemsList, nodesToToggle);
    this.#updateViews(newItemsExpanded);
  };

  #updateViews = (newItemsList: ItemNode[]) => {
    this.nodeVersionsListTreeModel = convertItemsListToTreeViewModel(
      newItemsList,
      this.checkboxType
    );

    this.nodeListFlattenedTreeModel =
      convertItemsNodeListToFlattenedTreeViewModel(
        newItemsList,
        this.checkboxType
      );
  };

  #viewItemsAsChangedHandler = async (event: CustomEvent<string>) => {
    const selectedViewType = event.detail as NodesVersionsViewType;
    this.nodesVersionsViewType = selectedViewType;
    if (this.nodesVersionsViewType === "tree") {
      await this.#toggleAndSelectNodesInModelItems(this.#selectedNodesIds);
      await this.#toggleCheckedNodesInModelItems(this.checkedNodesIds);
    }
    this.#updateViews(this.#updatedModelList);
  };

  #toggleCheckboxesChangedHandler = (
    event: ChCheckboxCustomEvent<string> | InputEvent
  ) => {
    this.toggleCheckboxes = event.detail === TOGGLE_CHECKBOXES_CHECKED_VALUE;
  };

  #scrollIntoActiveVersionNode = async () => {
    const doScrollIntoView =
      this.#treeViewRenderEl && this.activeItemNode?.path;
    if (doScrollIntoView) {
      await this.#treeViewRenderEl.scrollIntoVisible(this.activeItemNode.path);
    }

    if (this.activeItemCallback) {
      await this.activeItemCallback();
    }
  };

  render() {
    return (
      <Host class={{ "widget": true, "has-footer": this.renderFooter }}>
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>
        <section class="section">
          <header
            part="header"
            class="header dialog-header field-group spacing-body-block-start spacing-body-inline"
          >
            <div class="header__first-row field-group">
              <div class="field field-inline">
                <span>
                  {/*
                  WA: this span prevents field-inline applying padding-block-start on the label
                  TODO: See how to ignore padding-block-start when using field-inline with a control
                  that is not an input, combo-box, etc..
                  */}
                  <label class="label" htmlFor="view-mode">
                    Select view mode:
                  </label>
                </span>
                <ch-radio-group-render
                  class="radio-group dummy-control-block-size"
                  id="view-mode"
                  model={this.#viewItemsAsModel}
                  onChange={this.#viewItemsAsChangedHandler}
                  value={this.#viewItemsAsModel[0].value}
                ></ch-radio-group-render>
              </div>

              {this.checkboxType !== "none" && this.showToggleCheckboxes && (
                <ch-checkbox
                  class="checkbox dummy-control-block-size"
                  caption="Toggle Checkboxes"
                  id="toggle-checkboxes"
                  checkedValue={TOGGLE_CHECKBOXES_CHECKED_VALUE}
                  value={TOGGLE_CHECKBOXES_CHECKED_VALUE}
                  onInput={this.#toggleCheckboxesChangedHandler}
                ></ch-checkbox>
              )}
            </div>

            <div
              class={{
                "field-group": true,
                "header__second-row": true,
                "header__second-row--one-row": !this.showActiveItemLink
              }}
            >
              <ch-edit
                class="input"
                placeholder={
                  this.#_componentLocale.header.searchPatternPlaceholder
                }
                startImgSrc={SYSTEM_SEARCH_ICON}
                onInput={this.#patternSearchValueChangedHandler}
                accessibleName="search pattern"
              ></ch-edit>
              {this.activeItemNode && this.showActiveItemLink && (
                <div class="active-item">
                  <span
                    class="active-item__icon-container"
                    // make this span the icon container, to prevent icon state changes when
                    // interacting with div.active-item
                  >
                    <ch-image
                      class="icon-m active-item__icon"
                      src={
                        this.activeItemNode.iconStart ||
                        ACTIVE_ITEM_DEFAULT_ICON
                      }
                      containerRef={null}
                    ></ch-image>
                  </span>
                  {this.#_componentLocale.header.activeItem}
                  <button
                    class="active-item__link"
                    onClick={this.#scrollIntoActiveVersionNode}
                  >
                    {this.activeItemNode.caption}
                  </button>
                </div>
              )}
            </div>
          </header>

          <div class="spacing-body-inline-start trees">
            {this.useLoader && this.loading ? (
              <gx-ide-loader
                loaderTitle={this.#_componentLocale.loader.title}
                description={this.#_componentLocale.loader.description}
                show
              ></gx-ide-loader>
            ) : (
              <ch-tree-view-render
                class="tree-view"
                model={
                  this.nodesVersionsViewType === "tree"
                    ? this.nodeVersionsListTreeModel
                    : this.nodeListFlattenedTreeModel
                }
                show-lines={
                  this.nodesVersionsViewType === "tree" ? "all" : "none"
                }
                expandableButton={
                  this.nodesVersionsViewType === "tree" ? "decorative" : "no"
                }
                onItemContextmenu={this.#contextMenuHandler}
                onCheckedItemsChange={this.#checkedItemsChangeHandler}
                onSelectedItemsChange={this.#selectedItemsChangeHandler}
                expandOnClick={this.expandOnClick}
                multiSelection={this.multiSelection}
                filter={this.searchPatternValue}
                filterType="caption"
                toggleCheckboxes={this.toggleCheckboxes}
                ref={(el: HTMLChTreeViewRenderElement) => {
                  this.#treeViewRenderEl = el as HTMLChTreeViewRenderElement;
                }}
              ></ch-tree-view-render>
            )}
          </div>
          {this.renderFooter && this.#renderFooter()}
        </section>
      </Host>
    );
  }
}

export type CheckboxType = "none" | "all" | "leaf" | "custom";

export type DialogAction = {
  name: string;
  callback: DialogActionCallback;
};

export type DialogActionCallback = (...args: any[]) => void;

export type ItemCommon = {
  icon: string;
  expanded: boolean;
};

export type ItemNode = {
  active?: boolean;
  checkbox?: boolean;
  checked?: boolean;
  expanded?: boolean;
  iconStart?: string;
  iconEnd?: string;
  id: string;
  name: string;
  guid?: string;
  nodes?: ItemNode[];
  parts?: string;
  selected?: boolean;
  type?: ItemType;
  order?: number;
};

export type ActiveNode = {
  caption: string;
  iconStart?: string;
  path: string[]; // for using the scrollIntoVisible method of the tree-view-render
};

export type ItemType = {
  id: string;
  name: string;
};

type NodesVersionsViewType = "tree" | "list";
